import Tabs from "../../components/General/ResponsiveTabs/ResponsiveTabs";
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../api/apiVariable";
import useAuth from "../../hooks/useAuth";
import { DriverDetailsDto } from "../../interfaces/Fleet/DriverDetailsDto";
import EquipmentInventoryLists from "../../components/Fleet/EquipmentInventory/EquipmentInventoryLists";

const fetchDriverDetails = async (
  token: string | null | undefined,
  driverId: string,
): Promise<DriverDetailsDto> => {
  const { data } = await axios.get(
    `${apiVariable}/getDriverDetails/${driverId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data;
};

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

const formatBoolean = (value: boolean) => (value ? "Yes" : "No");

const EmptyState = ({ message }: { message: string }) => (
  <Alert severity="info" sx={{ mt: 2 }}>
    {message}
  </Alert>
);

const DetailField = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
      {value || "—"}
    </Typography>
  </Grid>
);

const currentColumn: GridColDef = {
  field: "isCurrent",
  headerName: "Current",
  width: 110,
  renderCell: (params) =>
    params.value ? <Chip color="success" label="Current" size="small" /> : "—",
};

const assignmentDateColumns: GridColDef[] = [
  {
    field: "dateAssigned",
    headerName: "Date Assigned",
    width: 160,
    valueFormatter: (value: string) => formatDate(value),
  },
  {
    field: "dateUnassigned",
    headerName: "Date Unassigned",
    width: 170,
    valueFormatter: (value?: string | null) => formatDate(value),
  },
];

const dispatcherAssignmentColumns: GridColDef[] = [
  currentColumn,
  {
    field: "name",
    headerName: "Dispatcher Name",
    flex: 1,
    minWidth: 180,
  },
  {
    field: "secondary",
    headerName: "Email",
    flex: 1,
    minWidth: 200,
  },
  {
    field: "status",
    headerName: "Status",
    width: 160,
  },
  ...assignmentDateColumns,
];

const truckAssignmentColumns: GridColDef[] = [
  currentColumn,
  {
    field: "name",
    headerName: "Truck Unit",
    flex: 1,
    minWidth: 180,
  },
  {
    field: "secondary",
    headerName: "VIN",
    flex: 1,
    minWidth: 220,
  },
  {
    field: "status",
    headerName: "Status",
    width: 150,
  },
  ...assignmentDateColumns,
];

const trailerAssignmentColumns: GridColDef[] = [
  currentColumn,
  {
    field: "name",
    headerName: "Trailer Unit",
    flex: 1,
    minWidth: 180,
  },
  {
    field: "secondary",
    headerName: "VIN",
    flex: 1,
    minWidth: 220,
  },
  {
    field: "status",
    headerName: "Status",
    width: 150,
  },
  ...assignmentDateColumns,
];

const recentCheckColumns: GridColDef[] = [
  {
    field: "date",
    headerName: "Date",
    width: 150,
    valueFormatter: (value: string) => formatDate(value),
  },
  {
    field: "isCertified",
    headerName: "Certified",
    width: 120,
    valueFormatter: (value: boolean) => formatBoolean(value),
  },
  {
    field: "hasViolation",
    headerName: "Violation",
    width: 120,
    valueFormatter: (value: boolean) => formatBoolean(value),
  },
  {
    field: "isDayOff",
    headerName: "Day Off",
    width: 110,
    valueFormatter: (value: boolean) => formatBoolean(value),
  },
  {
    field: "isShippingIdAdded",
    headerName: "Shipping ID",
    width: 130,
    valueFormatter: (value: boolean) => formatBoolean(value),
  },
  {
    field: "isTrailerIdAdded",
    headerName: "Trailer ID",
    width: 120,
    valueFormatter: (value: boolean) => formatBoolean(value),
  },
  {
    field: "isOverPersonalUsed",
    headerName: "Over Personal Use",
    width: 170,
    valueFormatter: (value: boolean) => formatBoolean(value),
  },
  { field: "truckUnitNumber", headerName: "Truck", width: 120 },
  { field: "trailerUnitNumber", headerName: "Trailer", width: 120 },
];

const recruitingIssueColumns: GridColDef[] = [
  {
    field: "dateOfIssueNotification",
    headerName: "Issue Date",
    width: 150,
    valueFormatter: (value: string) => formatDate(value),
  },
  { field: "priority", headerName: "Priority", width: 120 },
  { field: "status", headerName: "Status", width: 140 },
  { field: "contextOfConcern", headerName: "Concern", flex: 1, minWidth: 260 },
  {
    field: "actionsTaken",
    headerName: "Actions Taken",
    flex: 1,
    minWidth: 220,
  },
  {
    field: "resolutionDate",
    headerName: "Resolution Date",
    width: 160,
    valueFormatter: (value?: string | null) => formatDate(value),
  },
];

const DriverDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["driverDetails", id],
    queryFn: () => fetchDriverDetails(token, id as string),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box textAlign="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error">Error: {(error as Error).message}</Typography>
    );
  }

  if (!data) return <EmptyState message="No driver data available." />;

  const sortByDateAssignedDesc = <T extends { dateAssigned?: string | null }>(
    a: T,
    b: T,
  ) =>
    new Date(b.dateAssigned ?? 0).getTime() -
    new Date(a.dateAssigned ?? 0).getTime();

  const driver = data.driverInfo;
  const driverName =
    [driver.firstName, driver.lastName].filter(Boolean).join(" ") || "Driver";
  const dispatcherRows = data.dispatcherAssignments
    .map((assignment) => ({
      ...assignment,
      name: assignment.dispatcherName,
      secondary: assignment.dispatcherEmail,
      status: assignment.isCurrent ? "Active" : "Previous",
    }))
    .sort(sortByDateAssignedDesc); // Sort by current status first
  const truckRows = data.truckAssignments
    .map((assignment) => ({
      ...assignment,
      name: assignment.unitNumber,
      secondary: assignment.vin,
    }))
    .sort(sortByDateAssignedDesc);
  const trailerRows = data.trailerAssignments
    .map((assignment) => ({
      ...assignment,
      name: assignment.unitNumber,
      secondary: assignment.vin,
    }))
    .sort(sortByDateAssignedDesc);

  const metricCards = [
    ["Total Checks", data.logbookPerformance.totalChecks],
    ["Certified", data.logbookPerformance.certifiedChecks],
    ["Violations", data.logbookPerformance.violationChecks],
    ["Days Off", data.logbookPerformance.dayOffChecks],
    ["Missing Shipping ID", data.logbookPerformance.missingShippingIdChecks],
    ["Missing Trailer ID", data.logbookPerformance.missingTrailerIdChecks],
    ["Over Personal Use", data.logbookPerformance.overPersonalUseChecks],
    ["Last Check", formatDate(data.logbookPerformance.lastCheckDate)],
  ];

  return (
    <Box sx={{ mt: 4 }}>
      <Button
        component={RouterLink}
        to="/fleet/drivers"
        variant="outlined"
        sx={{ mb: 2 }}
      >
        Back to Drivers
      </Button>
      <Typography variant="h4" textAlign="center" gutterBottom>
        {driverName}
      </Typography>
      <Stack
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        useFlexGap
        direction="row"
        justifyContent="center"
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Chip
          label={driver.terminationDate ? "Inactive" : "Active"}
          color={driver.terminationDate ? "default" : "success"}
        />
        {driver.assignedDispatcher && (
          <Chip label={`Dispatcher: ${driver.assignedDispatcher}`} />
        )}
        {driver.assignedTruckUnitNumber && (
          <Chip label={`Truck: ${driver.assignedTruckUnitNumber}`} />
        )}
        {driver.assignedTrailerUnitNumber && (
          <Chip label={`Trailer: ${driver.assignedTrailerUnitNumber}`} />
        )}
      </Stack>

      <Tabs
        value={selectedTab}
        onChange={(_, value) => setSelectedTab(value)}
        centered
        sx={{ mb: 3 }}
      >
        <Tab label="Driver Info" />
        <Tab label="Dispatcher Assignments" />
        <Tab label="Truck Assignments" />
        <Tab label="Trailer Assignments" />
        <Tab label="Logbook Performance" />
        <Tab label="Recruiting Issues" />
        <Tab label="Equipment" />
      </Tabs>

      {selectedTab === 0 && (
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <DetailField label="Name" value={driverName} />
              <DetailField label="Phone" value={driver.phoneNumber} />
              <DetailField label="Email" value={(driver as any).email} />
              <DetailField
                label="Status"
                value={driver.terminationDate ? "Inactive" : "Active"}
              />
              <DetailField
                label="Hire Date"
                value={formatDate(driver.workStartDate)}
              />
              <DetailField label="Company" value={driver.company} />
              <DetailField
                label="Contract Type"
                value={
                  (driver as any).contractType || (driver as any).driverType
                }
              />
              <DetailField label="Driver Code" value={driver.driverCode} />
              <DetailField
                label="Termination Date"
                value={formatDate(driver.terminationDate)}
              />
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedTab === 1 &&
        (dispatcherRows.length ? (
          <DataGrid
            rows={dispatcherRows}
            columns={dispatcherAssignmentColumns}
            autoHeight
          />
        ) : (
          <EmptyState message="No dispatcher assignment history available." />
        ))}
      {selectedTab === 2 &&
        (truckRows.length ? (
          <DataGrid
            rows={truckRows}
            columns={truckAssignmentColumns}
            autoHeight
          />
        ) : (
          <EmptyState message="No truck assignment history available." />
        ))}
      {selectedTab === 3 &&
        (trailerRows.length ? (
          <DataGrid
            rows={trailerRows}
            columns={trailerAssignmentColumns}
            autoHeight
          />
        ) : (
          <EmptyState message="No trailer assignment history available." />
        ))}
      {selectedTab === 4 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {metricCards.map(([label, value]) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">
                      {label}
                    </Typography>
                    <Typography variant="h5">{value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {data.logbookPerformance.recentChecks.length ? (
            <DataGrid
              rows={data.logbookPerformance.recentChecks}
              columns={recentCheckColumns}
              autoHeight
            />
          ) : (
            <EmptyState message="No recent logbook checks available." />
          )}
        </Box>
      )}
      {selectedTab === 5 &&
        (data.recruitingIssues.length ? (
          <DataGrid
            rows={data.recruitingIssues}
            columns={recruitingIssueColumns}
            autoHeight
          />
        ) : (
          <EmptyState message="No recruiting issues available." />
        ))}
      {selectedTab === 6 && id && <EquipmentInventoryLists driverId={id} />}
    </Box>
  );
};

export default DriverDetailsPage;
